import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  RefreshCw, Search, Plus, Check, X, Truck, Package, 
  ArrowRight, Clock, AlertCircle, ChevronDown 
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getTransfers, getPendingApprovalCount, approveTransfer, rejectTransfer } from '../../api/stockTransfers';
import { getBranches } from '../../api/branches';
import { useAuth } from '../../contexts/AuthContext';
import CreateTransferModal from '../../components/inventory/CreateTransferModal';

const StockTransfersPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'retail_admin', 'system_admin'].includes(user?.role);

  const [transfers, setTransfers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all'); // all, pending, in_transit, completed
  const [filters, setFilters] = useState({
    branchId: searchParams.get('branchId') || '',
    status: ''
  });

  // Check for create transfer params
  const shouldOpenCreate = searchParams.get('create') === 'true';
  const defaultFromBranch = searchParams.get('fromBranch') || null;
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Rejection modal
  const [rejectModal, setRejectModal] = useState({
    isOpen: false,
    transferId: null,
    reason: ''
  });

  // Create transfer modal
  const [showCreateModal, setShowCreateModal] = useState(shouldOpenCreate);

  // Fetch branches for filter (with forTransfer to allow seeing all branches)
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await getBranches({ forTransfer: true });
        // Handle various API response formats
        let branchesArray = [];
        if (Array.isArray(response)) {
          branchesArray = response;
        } else if (Array.isArray(response?.branches)) {
          branchesArray = response.branches;
        } else if (Array.isArray(response?.data?.branches)) {
          branchesArray = response.data.branches;
        } else if (Array.isArray(response?.data)) {
          branchesArray = response.data;
        }
        setBranches(branchesArray);
      } catch (error) {
        console.error('Failed to load branches:', error);
      }
    };
    fetchBranches();
  }, []);

  // Fetch transfers
  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const statusFilter = activeTab === 'all' ? '' : activeTab;
      const [transfersData, pendingData] = await Promise.all([
        getTransfers({
          branchId: filters.branchId,
          status: statusFilter,
          limit: pagination.limit,
          offset: (pagination.page - 1) * pagination.limit
        }),
        isAdmin ? getPendingApprovalCount() : Promise.resolve({ count: 0 })
      ]);

      setTransfers(transfersData.transfers || []);
      setPagination(prev => ({
        ...prev,
        total: transfersData.total,
        totalPages: transfersData.pagination?.totalPages || 1
      }));
      setPendingCount(pendingData.count);
    } catch (error) {
      toast.error('Failed to load transfers');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters.branchId, pagination.page, pagination.limit, isAdmin]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  // Handle approve
  const handleApprove = async (transferId) => {
    if (!window.confirm('Approve this transfer?')) return;
    try {
      await approveTransfer(transferId);
      toast.success('Transfer approved');
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve');
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!rejectModal.reason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    try {
      await rejectTransfer(rejectModal.transferId, rejectModal.reason);
      toast.success('Transfer rejected');
      setRejectModal({ isOpen: false, transferId: null, reason: '' });
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      in_transit: 'bg-purple-100 text-purple-800',
      partially_received: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      draft: 'Draft',
      pending: 'Pending Approval',
      approved: 'Approved',
      in_transit: 'In Transit',
      partially_received: 'Partial',
      completed: 'Completed',
      cancelled: 'Cancelled',
      rejected: 'Rejected'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'text-gray-500',
      normal: 'text-blue-500',
      high: 'text-orange-500',
      urgent: 'text-red-500'
    };
    return priority !== 'normal' ? (
      <span className={`text-xs font-medium ${styles[priority]}`}>
        {priority.toUpperCase()}
      </span>
    ) : null;
  };

  const tabs = [
    { id: 'all', label: 'All Transfers', icon: RefreshCw },
    { id: 'pending', label: 'Pending Approval', icon: Clock, count: pendingCount },
    { id: 'in_transit', label: 'In Transit', icon: Truck },
    { id: 'completed', label: 'Completed', icon: Check }
  ];

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-primary-black">Stock Transfers</h1>
              <p className="text-xs sm:text-sm text-secondary-gray">
                Manage inter-branch stock movements
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600"
            >
              <Plus className="w-4 h-4" />
              New Transfer
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-4 overflow-x-auto">
          <div className="flex border-b">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setPagination(p => ({ ...p, page: 1 })); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-accent-gold text-accent-gold'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={filters.branchId}
              onChange={(e) => setFilters(prev => ({ ...prev, branchId: e.target.value }))}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
            >
              <option value="">All Branches</option>
              {Array.isArray(branches) && branches.map((branch) => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Transfers Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-accent-gold border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-secondary-gray">Loading transfers...</p>
            </div>
          ) : !Array.isArray(transfers) || transfers.length === 0 ? (
            <div className="p-8 text-center text-secondary-gray">
              <RefreshCw className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No transfers found</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600"
              >
                Create First Transfer
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-primary-black text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Transfer #</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">From → To</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Items</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Initiated</th>
                    {isAdmin && activeTab === 'pending' && (
                      <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transfers.map((transfer) => (
                    <tr 
                      key={transfer.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/stock-transfers/${transfer.id}`)}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium">{transfer.transfer_number}</p>
                        {getPriorityBadge(transfer.priority)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{transfer.from_branch_name}</span>
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{transfer.to_branch_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-medium">{transfer.item_count}</span>
                        <span className="text-gray-500 text-sm ml-1">
                          ({transfer.total_quantity} units)
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(transfer.status)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm">{transfer.initiated_by_name}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(transfer.initiated_at).toLocaleDateString()}
                        </p>
                      </td>
                      {isAdmin && activeTab === 'pending' && (
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleApprove(transfer.id)}
                              className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setRejectModal({ isOpen: true, transferId: transfer.id, reason: '' })}
                              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="border-t px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {pagination.page} of {pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {rejectModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Reject Transfer</h2>
            <p className="text-gray-600 mb-4">Please provide a reason for rejection:</p>
            <textarea
              value={rejectModal.reason}
              onChange={(e) => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold resize-none"
              rows={3}
              placeholder="Reason for rejection..."
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setRejectModal({ isOpen: false, transferId: null, reason: '' })}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reject Transfer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Transfer Modal */}
      <CreateTransferModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(transfer) => {
          setShowCreateModal(false);
          fetchTransfers();
          navigate(`/stock-transfers/${transfer.id}`);
        }}
        defaultFromBranchId={defaultFromBranch}
      />
    </div>
  );
};

export default StockTransfersPage;
